import * as React from 'react'
import ListItem from '@mui/material/ListItem'
import { EntityIcon } from '../SearchResultsIcon'
import ListItemText from '@mui/material/ListItemText'
import { useData, useHandlers } from './hooks'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import { LocationLink } from '../LocationLink/LocationLink'
import ListItemButton from '@mui/material/ListItemButton'
import Collapse from '@mui/material/Collapse'
import { HighlightedText, Chevron } from '@sales-center/components'
import { Props } from './typings'
import { ExtraInfo } from './ExtraInfo/ExtraInfo'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import { useMatchingValues } from '@user/features/GlobalSearch/ResultItem/hooks/useMatchingValues'
import { formatUSPhoneNumber } from '@user/features/GlobalSearch/ResultItem/utils'
import { CustomResults } from '@user/features/GlobalSearch/ResultItem/ExtraInfo/customResults'
import { useCheckCustomizeSettings } from '@user/features/GlobalSearch/ResultItem/hooks/useCheckCustomizeSettings'
import { Theme } from '@mui/material/styles'
import AddressMapLink from '@user/features/MapView/AddressMapLink/AddressMapLink'
import { mapEntityTypeToEnum } from './utils/functions'

export function ResultItem(props: Props) {
  const {
    onLocationClick,
    searchItem,
    searchText,
    recent,
    onSearchAddressClick,
  } = props
  const {
    entityType,
    locationCode,
    externalLinkId,
    externalLinkType,
    email,
    phone,
    address,
    entityId,
    latitude,
    longitude,
    serviceLocationId,
  } = searchItem
  const {
    entityTypeDisplay,
    toggleOpen,
    open,
    nameAndCompanyDisplay,
    canadaZipAwareSearchText,
  } = useData(props)
  const emailDisplay = useMatchingValues({
    searchText,
    email,
  })
  const { handleItemClick, toggleCollapse } = useHandlers({
    toggleOpen,
    ...props,
  })

  const { emailShow } = useCheckCustomizeSettings(searchItem)

  const highlightColor = '#e1ebf6'

  const entityTypeForItem = React.useMemo(
    () => mapEntityTypeToEnum(entityType),
    [entityType],
  )

  return (
    <>
      <ListItem
        disablePadding
        sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}
      >
        <ListItemButton
          onClick={toggleCollapse}
          sx={{
            padding: (theme: Theme) => theme.spacing(0),
            paddingLeft: (theme: Theme) => theme.spacing(recent ? 0.8 : 1),
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            width: '100%',
            marginLeft: (theme: Theme) => theme.spacing(-1),
          }}
        >
          <ListItemText>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              {entityType && (
                <EntityIcon entityType={recent ? 'recent' : entityType} />
              )}
              <Box
                sx={{
                  flexGrow: 1,
                  flexDirection: 'column',
                  ml: entityType ? 1 : 0,
                }}
              >
                <Stack direction="row" alignItems="center">
                  <Typography
                    onClick={handleItemClick}
                    variant="subtitle3"
                    sx={{
                      display: 'inline',
                      color: '#1565c0',
                    }}
                  >
                    <HighlightedText
                      highlightColor={highlightColor}
                      text={nameAndCompanyDisplay}
                      searchText={searchText}
                    />
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: (theme: Theme) => theme.palette.text.secondary,
                      marginLeft: (theme: Theme) => theme.spacing(1),
                      marginTop: (theme: Theme) => theme.spacing(0.3),
                    }}
                  >
                    {entityTypeDisplay}
                  </Typography>
                </Stack>
                {!recent ? (
                  <>
                    <Box>
                      <Typography variant="body2">
                        <AddressMapLink
                          address={address ?? ''}
                          entityId={entityId}
                          entityType={entityTypeForItem}
                          latitude={latitude}
                          longitude={longitude}
                          onClick={() => {
                            if (onSearchAddressClick && latitude && longitude) {
                              onSearchAddressClick({
                                location: new H.geo.Point(
                                  latitude ?? 0,
                                  longitude ?? 0,
                                ),
                                entityId,
                                entityType: entityTypeForItem,
                                serviceLocationId,
                              })
                            }
                          }}
                        >
                          <HighlightedText
                            highlightColor={highlightColor}
                            text={address}
                            searchText={canadaZipAwareSearchText}
                          />
                        </AddressMapLink>

                        {address && phone && (
                          <Typography
                            variant="body2"
                            component="span"
                            sx={{ margin: '0 8px' }}
                          >
                            •
                          </Typography>
                        )}

                        {formatUSPhoneNumber(phone) && (
                          <HighlightedText
                            searchText={formatUSPhoneNumber(searchText)}
                            highlightColor={highlightColor}
                            text={formatUSPhoneNumber(phone)}
                            phone
                          />
                        )}
                      </Typography>
                    </Box>
                    {entityType !== 'DraftLead' && locationCode && (
                      <Box>
                        <LocationLink
                          highlightColor={highlightColor}
                          locationCode={locationCode}
                          externalLinkId={externalLinkId}
                          externalLinkType={externalLinkType}
                          onClick={onLocationClick}
                          searchText={searchText}
                        />
                      </Box>
                    )}
                    {!emailShow && emailDisplay && (
                      <Box>
                        <Typography variant="body2">
                          <HighlightedText
                            highlightColor={highlightColor}
                            text={email}
                            searchText={searchText}
                          />
                        </Typography>
                      </Box>
                    )}
                    <CustomResults {...props} highlightColor={highlightColor} />
                  </>
                ) : (
                  <CustomResults {...props} highlightColor={highlightColor} />
                )}
              </Box>
              <Box sx={{ flexGrow: 0 }}>
                <IconButton
                  aria-label="expand"
                  onClick={toggleCollapse}
                  size="small"
                  sx={{ height: '32px', width: '32px' }}
                  data-test-id="globalSearchResultExpandBtn"
                >
                  <Chevron
                    open={open}
                    dataTestId="globalSearchResultExpandChevron"
                  />
                </IconButton>
              </Box>
            </Box>
          </ListItemText>
        </ListItemButton>
        <Collapse
          in={open}
          timeout="auto"
          unmountOnExit
          sx={{
            backgroundColor: (theme: Theme) => theme.palette.background.default,
            borderRadius: 2,
            padding: (theme: Theme) => theme.spacing(1),
            paddingLeft: recent ? 3.7 : 5,
            width: '100%',
          }}
        >
          <ExtraInfo highlightColor={highlightColor} {...props} />
        </Collapse>
      </ListItem>
    </>
  )
}
